import { Navigate, Outlet, RouteObject, useLocation } from 'react-router-dom';
import { ProtectedRoute } from '@sib/auth';
import {
  DefaultLayout,
  NavigationContextProvider,
  PageNotFound,
} from '@spendbrain/common/components';
import { EmptyPagePlaceholder } from '@spendbrain/common/components/EmptyPagePlaceholder';
import { paths } from '@spendbrain/config/paths';
import { SpendbrainRouteAccessContextProvider } from '@spendbrain/core/providers';
import { routes as authRoutes } from './auth';
import { routes as billingRoutes } from './billing';
import { routes as dashboardRoutes } from './dashboard';
import { routes as documentsRoutes } from './documents';
import { routes as helpRoutes } from './help';
import { routes as locationsRoutes } from './locations';
import { routes as reportsRoutes } from './reports';
import { routes as savingsRoutes } from './savings';
import { routes as servicesRoutes } from './services';
import { routes as subsidiaryOrganizationRoutes } from './subsidiaryOrganization';
import { routes as teamMembersRoutes } from './teamMembers';
import { routes as ticketsRoutes } from './tickets';
import { routes as vendorsRoutes } from './vendors';

export const routes: RouteObject[] = [
  ...authRoutes,
  {
    element: (
      <NavigationContextProvider>
        <SpendbrainRouteAccessContextProvider>
          <ProtectedRoute
            fallbackPath={paths.login}
            notFoundPath={paths.notFound}
            shouldGetAlternatePath={path => path === paths.dashboard}
          >
            <DefaultLayout
              attribute="class"
              defaultTheme="system"
              enableSystem
              disableTransitionOnChange
              footer={
                <div className="flex flex-col-reverse items-center justify-between gap-x-4 gap-y-2 sm:flex-row sm:gap-y-0">
                  <div className="flex flex-1 items-center justify-start gap-x-4 text-sm"></div>
                  <div className="flex flex-1 flex-col items-start justify-start gap-x-4 gap-y-1 sm:flex-row sm:justify-end sm:gap-y-0"></div>
                </div>
              }
              navCollapsedSize={4}
              logo={{
                default: (
                  <>
                    <div className="inline-flex items-center gap-x-4">
                      <img
                        src="/images/sibLogo.webp"
                        alt="Refine"
                        className="h-8 w-8"
                      />
                      <h3 className="text-xl font-bold">SIB Admin</h3>
                    </div>
                  </>
                ),
                collapsed: <img src="/images/sibLogo.webp" alt="Refine" />,
              }}
            >
              <Outlet />
            </DefaultLayout>
          </ProtectedRoute>
        </SpendbrainRouteAccessContextProvider>
      </NavigationContextProvider>
    ),
    // All protected routes go inside array below
    children: [
      ...dashboardRoutes,
      ...billingRoutes,
      ...documentsRoutes,
      ...helpRoutes,
      ...locationsRoutes,
      ...reportsRoutes,
      ...savingsRoutes,
      ...servicesRoutes,
      ...subsidiaryOrganizationRoutes,
      ...ticketsRoutes,
      ...vendorsRoutes,
      ...teamMembersRoutes,
      {
        path: paths.billPay,
        element: <EmptyPagePlaceholder />,
      },
      {
        path: paths.contractTerms,
        element: <EmptyPagePlaceholder />,
      },
      {
        path: paths.upcomingEvents,
        element: <EmptyPagePlaceholder />,
      },
      {
        path: paths.account,
        element: <EmptyPagePlaceholder />,
      },
      {
        path: paths.notFound,
        element: <PageNotFound />,
      },
      {
        path: paths.root,
        element: <RootDashboardRedirect />,
      },
      {
        path: paths.any,
        element: <Navigate to={paths.notFound} replace />,
      },
    ],
  },
];

function RootDashboardRedirect() {
  const { search, hash } = useLocation();

  return <Navigate to={`${paths.dashboard}${search}${hash}`} replace />;
}
